/* jshint esversion: 6 */
/**
 * CMSSortableService class provides functionality for sorting elements within a CMS.
 * It initializes various services and configurations for sorting operations.
 */
export default class CMSSortableService {
    /**
     * Constructor for CMSSortableService.
     * @param {Object} $bridges - Service for bridge operations.
     * @param {Object} $signals - Service for signal operations.
     * @param {Object} $translate - Service for translation.
     * @param {Object} TaskDataService - Service for task data operations.
     * @param {Object} $routeParams - Service for route parameters.
     * @param {Object} $rootScope - Service for root scope operations.
     * @param {Object} CopyContainerHelperService - Service for copy container helper operations.
     * @param {Object} $compile - Service for compiling HTML.
     */
    constructor($bridges, $signals, $translate, TaskDataService, $routeParams, $rootScope, CopyContainerHelperService, $compile) {
        // 'ngInject'; // Remove this line if present
        this.$bridges = $bridges;
        this.$signals = $signals;
        this.$translate = $translate;
        this.TaskDataService = TaskDataService;
        this.$routeParams = $routeParams;
        this.$rootScope = $rootScope;
        this.CopyContainerHelperService = CopyContainerHelperService;
        this.$compile = $compile;

        this._taskContext = TaskDataService.fetchContext();
        this._sheet = $routeParams.sheetUUID ? this._taskContext.read($routeParams.sheetUUID) : null;
        this._logIdentifier = 'CMSSortableService';
        this.$logger = $bridges.logger();
        this.newElements = null;
        this.copiedElements = null;

        $rootScope.$on('$routeChangeSuccess', () => {
            this.initNewElement();
            this.initCopiedElement();
        });

        this.initNewElement();
        this.initCopiedElement();

        this.excludeObject = {
            carousel: {
                "measurement": ['col50', 'col100'],
                "universalcanvas": ['col50', 'col100'],
                "scattergraph": ['col50', 'col100'],
                "auto-plot-from-table": ['col50', 'col100'],
                "wiris": ['col50', 'col100'],
                "wirisstimulus": ['col50', 'col100']
            },
            tab: [],
            container: {
                "auto-plot-from-table": ['col50'],
                "scattergraph": ['col50']
            },
            iaaContainer: {}
        };

        this.connectWith = {
            row: "row-wrapper",
            col: "col-wrapper",
            container: "container-wrapper",
            object: "object-wrapper"
        };

        this.helper = {
            row: () => $('<div class="row-helper helper"><span class="info-message"></span><span class="warning-message"></span></div>'),
            col: () => $('<div class="col-helper helper"><span class="info-message"></span><span class="warning-message"></span></div>'),
            container: () => $('<div class="container-helper helper"><span class="info-message"></span><span class="warning-message"></span></div>'),
            object: () => $('<div class="object-helper helper"><span class="info-message"></span><span class="warning-message"></span></div>'),
            new_row: () => $('<div class="new-row-helper helper"><span class="info-message"></span><span class="warning-message"></span></div>'),
            new_col: () => $('<div class="new-col-helper helper"><span class="info-message"></span><span class="warning-message"></span></div>'),
            new_container: () => $('<div class="new-container-helper helper"><span class="info-message"></span><span class="warning-message"></span></div>'),
            new_object: () => $('<div class="new-object-helper helper"><span class="info-message"></span><span class="warning-message"></span></div>'),
            new_container_copy: () => $('<div class="new-container-helper helper"><span class="info-message"></span><span class="warning-message"></span></div>')
        };

        this.placeholder = {
            row: "row-placeholder",
            col: "col-placeholder",
            container: "container-placeholder",
            containerCopy: "container-placeholder",
            object: "object-placeholder"
        };

        this.handle = {
            row: "row-handle",
            col: "col-handle",
            container: "container-handle",
            containerCopy: "container-handle",
            object: "object-handle"
        };

        this.sortableOption = {
            row: {
                cursorAt: {top: 0, left: 0},
                scrollSensitivity: 100,
                scrollSpeed: 25,
                tolerance: "pointer",
                cursor: "move",
                revert: 100,
                connectWith: this.connectWith.row ? "." + this.connectWith.row : false,
                helper: this.helper.row ? this.helper.row : 'original',
                placeholder: this.placeholder.row ? this.placeholder.row : false,
                handle: this.handle.row ? "." + this.handle.row : false,
                start: this.startRow ? this.startRow : function () {},
                sort: this.sortRow ? this.sortRow : function () {},
                stop: this.stopRow ? this.stopRow : function () {},
                update: this.updateRow ? this.updateRow : function () {},
                beforeStop: function (event, ui) {
                    if (ui.item.cancel) {
                        ui.helper.remove();
                    }
                }
            },
            col: {
                maxItems: 2,
                cursorAt: {top: 0, left: 0},
                tolerance: "pointer",
                cursor: "move",
                revert: 100,
                connectWith: this.connectWith.col ? "." + this.connectWith.col : false,
                helper: this.helper.col ? this.helper.col : 'original',
                placeholder: this.placeholder.col ? this.placeholder.col : false,
                handle: this.handle.col ? "." + this.handle.col : false,
                start: this.startCol ? this.startCol : function () {},
                sort: this.sortCol ? this.sortCol : function () {},
                stop: this.stopCol ? this.stopCol : function () {},
                update: this.updateCol ? this.updateCol : function () {},
                beforeStop: function (event, ui) {
                    if (ui.item.cancel) {
                        ui.helper.remove();
                    }
                }
            },
            container: {
                cursorAt: {top: 0, left: 0},
                tolerance: "pointer",
                cursor: "move",
                revert: 100,
                connectWith: this.connectWith.container ? "." + this.connectWith.container : false,
                helper: this.helper.container ? this.helper.container : 'original',
                placeholder: this.placeholder.container ? this.placeholder.container : false,
                handle: this.handle.container ? "." + this.handle.container : false,
                start: this.startContainer ? this.startContainer : function () {},
                sort: this.sortContainer ? this.sortContainer : function () {},
                stop: this.stopContainer ? this.stopContainer : function () {},
                update: this.updateContainer ? this.updateContainer : function () {},
                beforeStop: function (event, ui) {
                    if (ui.item.cancel) {
                        ui.helper.remove();
                    }
                }
            },
            containerCopy: {
                cursorAt: {top: 0, left: 0},
                tolerance: "pointer",
                cursor: "move",
                revert: 100,
                connectWith: this.connectWith.container ? "." + this.connectWith.container : false,
                helper: this.helper.containerCopy ? this.helper.containerCopy : 'original',
                placeholder: this.placeholder.container ? this.placeholder.container : false,
                handle: this.handle.container ? "." + this.handle.container : false,
                start: this.startContainerCopy ? this.startContainerCopy : function () {},
                sort: this.sortContainerCopy ? this.sortContainerCopy : function () {},
                stop: this.stopContainerCopy ? this.stopContainerCopy : function () {},
                update: this.updateContainerCopy ? this.updateContainerCopy : function () {},
                beforeStop: function (event, ui) {
                    if (ui.item.cancel) {
                        ui.helper.remove();
                    }
                }
            },
            object: {
                cursorAt: {top: 0, left: 0},
                tolerance: "pointer",
                zIndex: 1000,
                cursor: "move",
                revert: 100,
                connectWith: this.connectWith.object ? "." + this.connectWith.object : false,
                helper: this.helper.object ? this.helper.object : 'original',
                placeholder: this.placeholder.object ? this.placeholder.object : false,
                handle: this.handle.object ? "." + this.handle.object : false,
                start: this.startObject ? this.startObject : function () {},
                sort: this.sortObject ? this.sortObject : function () {},
                stop: this.stopObject ? this.stopObject : function () {},
                update: this.updateObject ? this.updateObject : function () {},
                beforeStop: function (event, ui) {
                    if (ui.item.cancel) {
                        ui.helper.remove();
                    }
                }
            },
            new_row: {
                cursorAt: {top: 0, left: 0},
                tolerance: "pointer",
                cursor: "move",
                revert: 100,
                helper: 'clone',
                connectWith: this.connectWith.row ? "." + this.connectWith.row : false,
                placeholder: this.placeholder.row ? this.placeholder.row : false,
                start: (event, ui) => {
                    ui.helper.addClass("new-row-helper helper");
                    ui.helper.find('.new-element-right').html('<span class="info-message"></span><span class="warning-message">');
                    $(".new-elements .new-element").not(ui.helper).removeAttr("style");
                    if (this.startRow) {
                        this.startRow(event, ui);
                    }
                    if (ui.helper.width() < 200) {
                        ui.helper.css({
                            minWidth: '100%'
                        });
                    }
                },
                sort: (event, ui) => {
                    $(".row-placeholder").css("display", "block");
                    $(".new-elements .row-placeholder").css("display", "none");
                    ui.helper.css({
                        width: ui.placeholder.width() > 155 ? ui.placeholder.width() : 155,
                        height: ui.placeholder.height() > 200 ? ui.placeholder.height() : 100
                    });
                    if (this.sortRow) {
                        this.sortRow(event, ui);
                    }
                },
                stop: (event, ui) => {
                    if (this.stopRow) {
                        this.stopRow(event, ui);
                    }
                    this.$signals.emit("sortable_stop_new_element");
                },
                update: (event, ui) => {
                    if (this.updateRow) {
                        this.updateRow(event, ui);
                    }
                },
                beforeStop: (event, ui) => {
                    if (ui.item.cancel) {
                        ui.helper.remove();
                    }
                }
            },
            new_col: {
                cursorAt: {top: 0, left: 0},
                tolerance: "pointer",
                cursor: "move",
                revert: 100,
                helper: 'clone',
                connectWith: this.connectWith.col ? "." + this.connectWith.col : false,
                placeholder: this.placeholder.col ? this.placeholder.col : false,
                start: (event, ui) => {
                    ui.helper.addClass("new-col-helper helper");
                    ui.helper.find('.new-element-right').html('<span class="info-message"></span><span class="warning-message">');
                    $(".new-elements .new-element").not(ui.helper).removeAttr("style");
                    if (this.startCol) {
                        this.startCol(event, ui);
                    }
                    if (ui.helper.width() < 200) {
                        ui.helper.css({
                            minWidth: '40%'
                        });
                    }
                },
                sort: (event, ui) => {
                    $(".col-placeholder").css("display", "block");
                    $(".new-elements .col-placeholder").css("display", "none");
                    ui.helper.css({
                        width: ui.placeholder.width() > 155 ? ui.placeholder.width() : 155,
                        height: ui.placeholder.height() > 200 ? ui.placeholder.height() : 100
                    });
                    if (this.sortCol) {
                        this.sortCol(event, ui);
                    }
                },
                stop: (event, ui) => {
                    if (this.stopCol) {
                        this.stopCol(event, ui);
                    }
                    this.$signals.emit("sortable_stop_new_element");
                },
                update: (event, ui) => {
                    if (this.updateCol) {
                        this.updateCol(event, ui);
                    }
                },
                beforeStop: (event, ui) => {
                    if (ui.item.cancel) {
                        ui.helper.remove();
                    }
                }
            },
            new_container: {
                cursorAt: {top: 0, left: 0},
                tolerance: "pointer",
                cursor: "move",
                revert: 100,
                helper: 'clone',
                connectWith: this.connectWith.container ? "." + this.connectWith.container : false,
                placeholder: this.placeholder.container ? this.placeholder.container : false,
                start: (event, ui) => {
                    ui.helper.addClass("new-container-helper helper");
                    ui.helper.find('.new-element-right').html('<span class="info-message"></span><span class="warning-message">');
                    $(".new-elements .new-element").not(ui.helper).removeAttr("style");
                    if (this.startContainer) {
                        this.startContainer(event, ui);
                    }
                    if (ui.helper.width() < 200) {
                        ui.helper.css({
                            minWidth: '50%'
                        });
                    }
                },
                sort: (event, ui) => {
                    $(".container-placeholder").css("display", "block");
                    $(".new-elements .container-placeholder").css("display", "none");
                    ui.helper.css({
                        width: ui.placeholder.width() > 155 ? ui.placeholder.width() : 155,
                        height: ui.placeholder.height() > 200 ? ui.placeholder.height() : 100
                    });
                    if (this.sortContainer) {
                        this.sortContainer(event, ui);
                    }
                },
                stop: (event, ui) => {
                    if (this.stopContainer) {
                        this.stopContainer(event, ui);
                    }
                    this.$signals.emit("sortable_stop_new_element");
                },
                update: (event, ui) => {
                    if (this.updateContainer) {
                        this.updateContainer(event, ui);
                    }
                },
                beforeStop: (event, ui) => {
                    if (ui.item.cancel) {
                        ui.helper.remove();
                    }
                }
            },
            new_container_copy: {
                cursorAt: {top: 0, left: 0},
                tolerance: "pointer",
                cursor: "move",
                revert: 100,
                helper: 'clone',
                connectWith: this.connectWith.container ? "." + this.connectWith.container : false,
                placeholder: this.placeholder.container ? this.placeholder.container : false,
                start: (event, ui) => {
                    ui.helper.addClass("new-container-helper helper");
                    ui.helper.find('.new-element-right').html('<span class="info-message"></span><span class="warning-message"></span>');

                    $(".new-elements .new-element").not(ui.helper).removeAttr("style");
                    if (this.startContainer) {
                        this.startContainer(event, ui);
                    }
                    if (ui.helper.width() < 200) {
                        ui.helper.css({
                            minWidth: '50%'
                        });
                    }
                },
                sort: (event, ui) => {
                    $(".container-placeholder").css("display", "block");
                    $(".new-elements .container-placeholder").css("display", "none");

                    ui.helper.css({
                        width: ui.placeholder.width() > 155 ? ui.placeholder.width() : 155,
                        height: ui.placeholder.height() > 200 ? ui.placeholder.height() : 100
                    });

                    if (this.sortContainer) {
                        this.sortContainer(event, ui);
                    }
                },
                stop: (event, ui) => {
                    if (this.stopContainer) {
                        this.stopContainer(event, ui);
                    }

                    this.$signals.emit("sortable_stop_new_element");
                },
                update: (event, ui) => {
                    if (this.updateContainer) {
                        this.updateContainer(event, ui);
                    }
                },
                beforeStop: (event, ui) => {
                    if (ui.item.cancel) {
                        ui.helper.remove();
                    }
                }
            }
        };

        this.sortableOption.new_tab = this.sortableOption.new_container;
        this.sortableOption.new_carousel = this.sortableOption.new_container;
        this.sortableOption.new_iaaContainer = this.sortableOption.new_container;
    }

    /**
     * Getter
     */

    /**
     * Get sortableOption Object
     * @param {string} option - The key of the sortable option to retrieve.
     * @returns {Object|null} - The sortable option object or null if not found.
     */
    getSortableOption(option) {
        if (typeof option === 'undefined') {
            return this.sortableOption;
        }
        if (typeof this.sortableOption[option] === 'undefined') {
            return null;
        }
        return this.sortableOption[option];
    }

    /**
     * Get connectWith Object
     * @param {string} index - The key of the connectWith option to retrieve.
     * @returns {Object|null} - The connectWith option object or null if not found.
     */
    getConnectWith(index) {
        if (typeof index === 'undefined') {
            return this.connectWith;
        }
        if (typeof this.connectWith[index] === 'undefined') {
            return null;
        }
        return this.connectWith[index];
    }

    /**
     * Get onMouseDown Function
     * @returns {Function} - The onMouseDown function.
     */
    getOnMouseDown() {
        return function (elem) {
            $(".row-" + elem).find(".row-content").css({
                height: 0,
                overflow: 'hidden',
                display: 'none'
            });
        };
    }

    /**
     * Get onMouseUp Function
     * @returns {Function} - The onMouseUp function.
     */
    getOnMouseUp() {
        return function (elem) {
            $(".row-" + elem).find(".row-content").removeAttr("style");
        };
    }

    /**
     * Get Rows Object
     * @returns {Object} - The rows object.
     */
    getRowsObj() {
        return this.rows;
    }

    /**
     * Get new elements (Row, Col, Container)
     * @returns {Object} - The new elements object.
     */
    getNewElements() {
        this.initNewElement();
        return angular.copy(this.newElements);
    }

    /**
     * Get copied elements (Row, Col, Container)
     * @param {number} numb - The number of elements to retrieve.
     * @returns {Object} - The copied elements object.
     */
    getCopiedElements(numb) {
        this.initCopiedElement(numb);
        return angular.copy(this.copiedElements);
    }

    /**
     * Initialize new elements
     */
    initNewElement() {
        // Implementation of initNewElement method
    }

    /**
     * Initialize copied elements
     * @param {number} numb - The number of elements to initialize.
     */
    initCopiedElement(numb) {
        // Implementation of initCopiedElement method
    }
}

// Add explicit dependency injection
CMSSortableService.$inject = [
    '$bridges',
    '$signals',
    '$translate',
    'TaskDataService',
    '$routeParams',
    '$rootScope',
    'CopyContainerHelperService',
    '$compile'
];